import React, { useState, useEffect, useCallback } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { globalHistory } from "@reach/router"

import { useConfigContext } from "@app/providers/config"
import { useCore } from "@app/hooks/useCore"
import { useCartContext } from "@app/providers/cart"

export type ProductImpressionProps = {
  item_id: string
  item_name: string
  item_brand: string
  item_variant: string
  item_category: string
  item_list_name: string
  item_list_id: string
  index: number
  quantity: number | string
  price: number | string
}

export type ContextProps = {
  tracked: boolean
  setTracked: React.Dispatch<React.SetStateAction<boolean>>
  latestVersion: boolean
  colourOptionName: string
  gtm?: {
    push: (data: Record<string, unknown>) => void
  }
  setProductImpressionBatch: React.Dispatch<React.SetStateAction<Array<ProductImpressionProps>>>
  productImpressionBatch: Array<ProductImpressionProps>
  pushDataLayer: (datalayer: any) => void
}

const gtm = {
  push: (data: Record<string, unknown>) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(data)
  },
}

export const TrackingContext = React.createContext<ContextProps | undefined>(undefined)

export const TrackingProvider: React.FC = ({ children }) => {
  const {
    settings: {
      product: { colourOptionName },
      tagGateway,
    },
  } = useConfigContext()
  const {
    helpers: { isDomReady, isDev },
  } = useCore()
  const { currencyCode } = useCartContext()
  const [tracked, setTracked] = useState(false)
  const [productImpressionBatch, setProductImpressionBatch] = useState<Array<ProductImpressionProps>>([])

  const { tracking } = useStaticQuery<GatsbyTypes.StaticTrackingQuery>(graphql`
    query StaticTracking {
      tracking: sanitySettingTracking {
        facebookAppId
        googleSiteVerify
        googleTagManagerId
      }
    }
  `)

  const pushDataLayer = useCallback(
    (dataLayer: any) => {
      isDev ? console.log({ ...dataLayer }) : gtm.push(dataLayer)
    },
    [isDev]
  )

  const contextValue = React.useMemo<ContextProps>(
    () => ({
      tracked,
      setTracked,
      productImpressionBatch,
      setProductImpressionBatch,
      latestVersion: true, //"GA4"
      colourOptionName, //"Colour"
      // Bots and audit tools (isDomReady false) get the context but never push events
      gtm: isDomReady ? gtm : undefined,
      pushDataLayer,
    }),
    [tracked, setTracked, colourOptionName, productImpressionBatch, pushDataLayer, setProductImpressionBatch, isDomReady]
  )

  useEffect(() => {
    if (tracking?.googleTagManagerId) {
      gtm.push({ "gtm.start": Date.now(), event: "gtm.js" })

      const script = document.createElement("script")
      script.async = true
      script.src = `${tagGateway.url}/?id=${tracking.googleTagManagerId}`
      script.onerror = () => {
        const fallback = document.createElement("script")
        fallback.async = true
        fallback.src = `https://www.googletagmanager.com/gtm.js?id=${tracking.googleTagManagerId}`
        document.head.appendChild(fallback)
      }
      document.head.appendChild(script)
      setTracked(true)
    }
  }, [tracking, tagGateway])

  useEffect(() => {
    if (productImpressionBatch.length <= 16 && productImpressionBatch.length > 11) {
      pushDataLayer({
        event: "view_item_list",
        ecommerce: {
          currency: currencyCode,
          items: productImpressionBatch,
        },
      })
      setProductImpressionBatch([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productImpressionBatch.length, currencyCode, pushDataLayer])

  useEffect(() => {
    return globalHistory.listen(({ action }) => {
      if (action === "PUSH" && productImpressionBatch.length) {
        pushDataLayer({
          event: "view_item_list",
          ecommerce: {
            currency: currencyCode,
            items: productImpressionBatch,
          },
        })
        setProductImpressionBatch([])
      }
    })
  }, [currencyCode, productImpressionBatch, pushDataLayer])

  return <TrackingContext.Provider value={contextValue}>{children}</TrackingContext.Provider>
}

export const useTrackingContext = (): ContextProps => ({ ...React.useContext(TrackingContext) }) as ContextProps
